import { Form, message } from "antd";
import { useFetcher, useNavigation, useSubmit } from "react-router-dom";
import * as APITypes from "~/API";
import { INTENTS, removeEmptyFields } from "~/utils";

type PaymentFormValues = {
  reason?: APITypes.TransactionReason;
  lease: APITypes.Lease;
  offer?: APITypes.Offer;
  serviceRequest?: APITypes.ServiceRequest[];
  transactions?: APITypes.Transaction[];
  history?: {
    month: number;
    year: number;
    paid: number;
    complete: boolean;
    expectedAmount?: number;
    periodStart?: string;
    periodEnd?: string;
  }[];
  amount: number;
};

export const useMakePaymentForm = (initialValues: PaymentFormValues) => {
  const [formInstance] = Form.useForm();
  const fetcher = useFetcher();
  const submit = useSubmit();
  const navigation = useNavigation();
  const isLoading = navigation.state !== "idle";

  const handleReasonChange = (value: APITypes.TransactionReason) => {
    console.log("handleReasonChange called");
    resetForm();
    if (value) {
      formInstance.setFieldValue("reason", value);
    }

    submit({ reason: value }, { method: "GET" });

    console.log("reason fetcher called");
  };

  const handleAmountChange = () => {
    formInstance.setFieldValue("amount", initialValues.offer?.rent ?? 0);
  };

  const handleRequestChange = (value: APITypes.ServiceRequest) => {
    formInstance.setFieldValue("amount", value.finalCost);
  };

  const getAmount = (): Number => {
    return formInstance.getFieldValue("amount") || 0;
  };

  const resetForm = () => {
    formInstance.resetFields();
  };

  const handleSubmit = (values: any) => {
    if (values.amount < 1 || values.amount === undefined) {
      message.error("Select a payment option");
      return;
    }

    let intent = INTENTS._make_payment;

    if (values.monthlyReport) {
      const [month, year] = values.monthlyReport.split("-");
      values.month = parseInt(month);
      values.year = parseInt(year);
      const historyItem = initialValues.history?.find(
        (item) =>
          item.month === values.month && item.year === values.year,
      );
      if (historyItem?.periodStart) {
        values.periodStart = historyItem.periodStart;
      }
      if (historyItem?.periodEnd) {
        values.periodEnd = historyItem.periodEnd;
      }
      values.monthlyReport = undefined;
    }

    if (values.utility) {
      values.transactionID = values.utility;
      intent = INTENTS._complete_payment;
    }

    values.managerID = initialValues.lease.userID;
    values.tenantID = initialValues.lease.tenantID;
    values.entityID = initialValues.lease.entityID;
    values.entityType = initialValues.lease.entityType;
    values.paymentMethod = APITypes.RentPaymentMethodType.Check;
    values.currency = "USD";

    const formValues = removeEmptyFields(values);
    fetcher.submit(
      { body: JSON.stringify(formValues), intent },
      { method: "POST" }
    );
  };

  const handleMonthChange = (value: string) => {
    const [monthStr, yearStr] = String(value).split("-");
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    const historyItem = initialValues.history?.find(
      (item) => item.month === month && item.year === year,
    );
    const expected =
      historyItem?.expectedAmount ?? initialValues.offer?.rent ?? 0;
    formInstance.setFieldValue("amount", expected);
    formInstance.setFieldValue("utility", undefined);
    formInstance.setFieldValue("serviceRequest", undefined);
  };

  const handleUtilityChange = (value: string) => {
    const transaction = initialValues.transactions?.find(
      (transaction) => transaction.id === value
    );
    const amount = transaction?.amount;
    formInstance.setFieldValue("amount", amount);
    formInstance.setFieldValue("monthlyReport", undefined);
    formInstance.setFieldValue("serviceRequest", undefined);
  };

  return {
    formInstance,
    fetcher,
    isLoading,
    resetForm,
    handleReasonChange,
    handleAmountChange,
    handleRequestChange,
    handleMonthChange,
    handleUtilityChange,
    getAmount,
    handleSubmit,
  };
};
